// @flow
import React, { Component } from 'react'
import styled, { css } from 'styled-components'
import { BOOKING_UPSELL } from 'src/helpers/constants'
import type { Theme as ThemeType } from 'src/types'
import { media } from 'src/theme/style-utils'
import Tooltip, { VARIATIONS } from 'src/components/Tooltip'
import { TOOLTIP_TYPES } from 'src/ducks/Tooltips/TooltipData'

type Props = {
  bookingDeposit: string,
  adminFee: string,
  membership: any,
  paymentPlan?: string,
  large?: boolean,
  hasUpsell?: ?boolean,
  noMargin?: ?boolean,
  coloredBg?: string,
  modalLayout?: boolean
}

const StyledPaymentSummary = styled.div`
  ${media.phone`
    margin: 20px 20px;
  `}
  
  ${media.tabletLarge`
    margin: 20px 40px;
  `}

  ${props => {
    if (props.modalLayout) {
      return css`
        ${media.phone`
          margin: 0 0 10px 0 !important;
        `}
        ${media.tabletLarge`
          margin: 20px 40px;
        `}
      `
    } else if (props.noMargin) {
      return css`
        margin: 10px 20px 0 20px !important;

        ${media.tabletLarge`
        margin: 10px 40px 0 40px !important;
      `}

        ${media.desktop`
        margin: 10px 0 0 0 !important;
      `}
      `
    }
  }};

`

const HorizRule = styled.hr`
  border-top: ${(props: { theme: ThemeType }) => `1px solid ${props.theme.secondary}`};
  margin-bottom: 0;
  margin-top: 0;

  ${media.desktop`
    border-top: none;
    margin-bottom: 0;
  `}

  ${props =>
    props.hideOnDesktop &&
    css`
      ${media.desktop`
        display: none;
      `}
    `};
`

const SummaryTable = styled.table`
  ${props =>
    props.hideOnDesktop &&
    css`
      ${media.desktop`
        display: none;
      `}
    `};

  width: 100%;

  ${props =>
    props.coloredBg !== '' &&
    css`
      padding: 10px 0 10px 0;

      ${media.desktop`
        padding: 10px 40px;
        background-color: ${props.coloredBg};
      `};
    `};

  ${props =>
    props.bottomPadded &&
    css`
      margin-top: -5px;
      padding-bottom: 20px;
    `};
`

const Arrow = styled.div`
  ${media.desktop`
    width: 0;
    height: 0;
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-top: 9px solid #000;
    margin-right: 8px;

    ${props =>
      !props.isOpen &&
      css`
        transform: rotate(270deg);
      `}
  `}
`

const SummaryRow = styled.tr`
  ${props =>
    props.clickable &&
    css`
      cursor: pointer;
      pointer-events: none;

      ${media.desktop`
        pointer-events: initial;
      `}
    `};

  td {
    padding: 5px 0;
  }

  ${props =>
    !props.clickable &&
    css`
      td {
        ${media.desktop`
          padding-left: 22px;
        `}
      }
    `};
`

const Label = styled.td`
  font-size: 14px;
  color: ${(props: { theme: ThemeType }) => props.theme.dark};

  ${props =>
    props.large &&
    css`
      font-family: 'RelativeMedium';
      font-size: 16px;
    `};

  ${props =>
    props.extraLarge &&
    css`
      color: ${(props: { theme: ThemeType }) => props.theme.darkText};
      font-family: 'RelativeMedium';
      font-size: 24px;
    `};

  ${props =>
    props.darker &&
    css`
      color: ${(props: { theme: ThemeType }) => props.theme.darkText};
    `};

  ${props =>
    props.darkerOnDesktop &&
    css`
      ${media.desktop`
        color: ${(props: { theme: ThemeType }) => props.theme.darkText};
      `}
    `};

  ${props =>
    props.collapsible &&
    css`
      display: flex;
      align-items: center;
      vertical-align: top;
      ${media.desktop`
        padding-bottom: 15px !important;
      `}
    `};
`

const Value = styled.td`
  font-size: 14px;
  color: ${(props: { theme: ThemeType }) => props.theme.darkText};
  text-align: right;

  ${props =>
    props.large &&
    css`
      font-family: 'RelativeMedium';
      font-size: 16px;
      color: ${(props: { theme: ThemeType }) => props.theme.darkText};
    `};

  ${props =>
    props.extraLarge &&
    css`
      font-family: 'RelativeMedium';
      font-size: 24px;
    `};

  ${props =>
    props.collapsible &&
    css`
      ${media.desktop`
        padding-bottom: 15px !important;
      `}
    `};
`

// const Heading = styled.header`
//   margin-top: 10px;
//   font-family: 'RelativeMedium';
//   font-size: 16px;
//   font-weight: 500;
//   color: ${(props: { theme: ThemeType }) => props.theme.darkText};

//   ${props =>
//     props.hideOnDesktop &&
//     css`
//       ${media.desktop`
//         display: none;
//       `}
//     `};

//   ${media.desktop`
//     padding: 0 40px 0 60px;
//   `};
// `

type State = { showPayNow: boolean, showRemaining: boolean }
class PaymentSummary extends Component<Props, State> {
  state = {
    showPayNow: true,
    showRemaining: true
  }

  toggleCollapse = (itemToToggle: string) => {
    this.setState({ [itemToToggle]: !this.state[itemToToggle] })
  }

  render() {
    // const { bookingDeposit, adminFee, membership, paymentPlan, hasUpsell, noMargin } = this.props
    const { bookingDeposit, adminFee, hasUpsell, noMargin, modalLayout = false } = this.props
    const fee = adminFee ? parseInt(adminFee, 10) : 0
    const deposit = bookingDeposit ? parseInt(bookingDeposit, 10) : 0

    // const monthly = Math.round((52 * membership.weeklyPrice) / 12)
    // const upfront = monthly * membership.value
    const toPayNow = hasUpsell ? fee + deposit + BOOKING_UPSELL : fee + deposit
    // const licenceFee = paymentPlan === 'full_upfront_2_weeks_security' ? upfront : monthly
    // let securityBond = 0
    // if (paymentPlan === 'monthly_5_weeks_security') {
    //   securityBond = membership.weeklyPrice * 5
    // } else {
    //   securityBond = membership.weeklyPrice * 2
    // }
    // const remainingBeforeMoveIn = licenceFee + securityBond - deposit

    return (
      <StyledPaymentSummary noMargin={noMargin ? true : false} modalLayout={modalLayout}>
        <HorizRule />
        {/* <Heading hideOnDesktop={true}>To apply for this room you&apos;ll pay</Heading> */}
        <SummaryTable coloredBg="#f0f3f5">
          <tbody>
            <SummaryRow clickable={true} onClick={() => this.toggleCollapse('showPayNow')}>
              <Label large darker collapsible>
                <Arrow isOpen={this.state.showPayNow} />
                <div>To pay now</div>
              </Label>
              <Value large collapsible>
                £{toPayNow}
              </Value>
            </SummaryRow>
            {this.state.showPayNow && (
              <React.Fragment>
                <SummaryRow>
                  <Label darkerOnDesktop>
                    Holding Deposit
                    <Tooltip variation={VARIATIONS.GREEN} type={TOOLTIP_TYPES.HOLDING_DEPOSIT} />
                  </Label>
                  <Value>{`£${deposit}`}</Value>
                </SummaryRow>
                <SummaryRow>
                  <Label darkerOnDesktop>Membership Joining Fee</Label>
                  <Value>{`£${fee}`}</Value>
                </SummaryRow>
              </React.Fragment>
            )}

            {hasUpsell && (
              <SummaryRow>
                <Label>Kitchen Cutlery Pack</Label>
                <Value>{`£${BOOKING_UPSELL}`}</Value>
              </SummaryRow>
            )}
          </tbody>
        </SummaryTable>

        {/* {paymentPlan && paymentPlan !== 'monthly' && (
          <React.Fragment>
            <HorizRule hideOnDesktop={true} />
            <SummaryTable coloredBg="#fff">
              <tbody>
                <SummaryRow clickable={true} onClick={() => this.toggleCollapse('showRemaining')}>
                  <Label large darker collapsible>
                    <Arrow isOpen={this.state.showRemaining} />
                    <div>Remaining before move-in</div>
                  </Label>
                  <Value large collapsible>
                    £{remainingBeforeMoveIn}
                  </Value>
                </SummaryRow>
                {this.state.showRemaining && (
                  <React.Fragment>
                    <SummaryRow>
                      <Label>Licence fee</Label>
                      <Value>£{licenceFee}</Value>
                    </SummaryRow>

                    <SummaryRow>
                      <Label>Security bond</Label>
                      <Value>£{securityBond}</Value>
                    </SummaryRow>
                  </React.Fragment>
                )}
              </tbody>
            </SummaryTable>

            {this.state.showRemaining && (
              <React.Fragment>
                <Heading>Deductions</Heading>
                <SummaryTable coloredBg="#fff">
                  <tbody>
                    <SummaryRow>
                      <Label>Holding deposit</Label>
                      <Value>{`-£${deposit}`}</Value>
                    </SummaryRow>
                  </tbody>
                </SummaryTable>
              </React.Fragment>
            )}
          </React.Fragment>
        )} */}
      </StyledPaymentSummary>
    )
  }
}

// $FlowFixMe
export default PaymentSummary
// export default connect()(PaymentSummary)



// WEBPACK FOOTER //
// ./src/components/PaymentSummary/index.js